import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./CurateResources.module.css";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import SelectionListPane, { Category } from "../../../Components/Utils/SelectionListPane/SelectionListPane";
import Paging from "../../../Components/Utils/Paging/Paging";
import DropDownList from "../../../Components/Utils/DropDownList/DropDownList";
import ConfirmAction from "../../../Components/Utils/ConfirmAction/ConfirmAction";
import PromptAction from "../../../Components/Utils/PromptAction/PromptAction";
import ContextMenu from "../../../Components/Utils/ContextMenu/ContextMenu";
import { ContextMenuItem } from "../../../Types/ContextMenuItem";
import {
  createCurateFolder,
  createCurateSource,
  clearCurateFolderContents,
  deleteCurateFolder,
  deleteCurateResource,
  getCurateAccessClients,
  getCurateAdminDirectory,
  getCurateFolderAccessClients,
  getCurateResourceStats,
  grantCurateAccess,
  grantCurateFolderAccess,
  refreshCurateResourceSummary,
  backfillCurateSourceSummaries,
  renameCurateFolder,
  updateCurateResource,
  uploadCurateResource,
  crawlCurateSource,
  getCurateSourcePages,
} from "../../../API/Curate/Curate";
import {
  CurateAdminDirectoryDto,
  CurateDocumentOutlineEntry,
  CurateProviderInfoDto,
  CurateResourceListItemDto,
  CurateResourceStatsDto,
} from "../../../Types/Curate/CurateResource";
import { toast } from "react-toastify";
import {
  ALLOWED_UPLOAD_ACCEPT,
  ALLOWED_UPLOAD_LABEL,
  rejectedFilesToast,
  splitAllowedFiles,
} from "../../../Helpers/uploadFileTypes";
import { IDropDownListItem } from "../../../Types/DropDownList/IDropDownListItem";
import {
  curateExportFilename,
  downloadCurateEngagementWorkbook,
  toCurateExportRow,
} from "../../../Utils/Export/curateEngagementExport";

const PAGE_SIZE = 40;
const ACCESS_PAGE_SIZE = 20;

const TLIPSS_URLS = [
  "https://employers.tlevels.gov.uk/hc/en-gb",
  "https://support.tlevels.gov.uk/hc/en-gb",
];

type AccessScope = "resource" | "site" | "folder";

type PendingSelectAll = {
  selecting: boolean;
  apply: () => void;
};

type SourceForm = {
  name: string;
  url: string;
  crawlChildren: boolean;
  maxPages: number;
};

const SUMMARY_STAGE_LABELS: Record<string, string> = {
  queued: "Queued for summary",
  fetching: "Fetching the page",
  reading: "Reading the document",
  writing: "Drafting an abstract",
};

const currentSummaryStageLabel = (stage: string | undefined, isUrl: boolean): string => {
  const current = (stage || "queued").toLowerCase();
  if (current === "fetching" && isUrl) return SUMMARY_STAGE_LABELS.fetching;
  if (current === "reading" && !isUrl) return SUMMARY_STAGE_LABELS.reading;
  if (current === "writing") return SUMMARY_STAGE_LABELS.writing;
  return SUMMARY_STAGE_LABELS.queued;
};

const openExternalUrl = (url: string, event?: React.MouseEvent) => {
  event?.preventDefault();
  event?.stopPropagation();
  window.open(url, "_blank", "noopener,noreferrer");
};

const ResourceOutline = ({ outline }: { outline?: CurateDocumentOutlineEntry[] }) => {
  if (!outline?.length) {
    return null;
  }

  return (
    <ul className={styles.outline}>
      {outline.map((entry) => (
        <li key={`${entry.startPage}-${entry.endPage}-${entry.heading}`}>
          {entry.startPage === entry.endPage
            ? `p. ${entry.startPage}`
            : `pp. ${entry.startPage}–${entry.endPage}`}
          {" — "}
          {entry.heading}
        </li>
      ))}
    </ul>
  );
};

const ResourceUrlLink = ({ url, className }: { url: string; className?: string }) => (
  <a
    className={className}
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(event) => event.stopPropagation()}
  >
    {url}
  </a>
);

const resourceAbstract = (resource: {
  summary?: string;
  description?: string;
}): string => (resource.summary || resource.description || "").trim();

const isPendingSummary = (resource: { summaryStatus?: string }): boolean =>
  (resource.summaryStatus || "").toLowerCase() === "pending";

const isFailedSummary = (resource: { summaryStatus?: string }): boolean =>
  (resource.summaryStatus || "").toLowerCase() === "failed";

const summaryEmptyMessage = (resource: { summaryStatus?: string }): string =>
  isPendingSummary(resource)
    ? "A summary is being generated."
    : "No description or summary is attached to this resource yet.";

const PendingSummaryProgress = ({ resource }: { resource: CurateResourceListItemDto }) => {
  const isUrl = resource.kind === "link" || resource.kind === "source";
  const stage = currentSummaryStageLabel(resource.summaryStage, isUrl);

  return (
    <div className={styles.summaryProgress} role="status" aria-live="polite">
      <span className={styles.summaryProgressTitle}>Generating summary</span>
      <div className={styles.summaryProgressTrack}>
        <div className={styles.summaryProgressBar} />
      </div>
      <span className={styles.summaryProgressStage}>{stage}</span>
    </div>
  );
};

const isCrawlActive = (resource: { crawlStatus?: string }): boolean => {
  const status = (resource.crawlStatus || "").toLowerCase();
  return status === "queued" || status === "running";
};

const crawlStatusLabel = (status?: string): string | null => {
  switch ((status || "").toLowerCase()) {
    case "queued":
      return "crawl queued";
    case "running":
      return "crawling";
    case "complete":
      return "crawled";
    case "failed":
      return "crawl failed";
    default:
      return null;
  }
};

const formatCtr = (recommendations: number, clicks: number): string =>
  recommendations <= 0 ? "—" : `${Math.round((clicks / recommendations) * 100)}%`;

const compactResourceMeta = (
  resource: CurateResourceListItemDto,
  isChild = false
): string => {
  const parts: string[] = [];
  if (isChild) {
    const pageCount = resource.pageCount ?? 0;
    if (pageCount > 0) {
      parts.push(`${pageCount} page${pageCount === 1 ? "" : "s"}`);
    }
    if (resource.isActive === false) parts.push("disabled");
  } else {
    const childCount = resource.childCount ?? 0;
    if (childCount > 0) {
      parts.push(`${childCount} item${childCount === 1 ? "" : "s"}`);
    }
    const crawlLabel = crawlStatusLabel(resource.crawlStatus);
    if (crawlLabel) parts.push(crawlLabel);
    if (resource.isActive === false) parts.push("inactive");
  }
  if (isPendingSummary(resource)) parts.push("summarising");
  else if (isFailedSummary(resource)) parts.push("summary failed");
  return parts.join(" · ");
};

const fetchAllSourcePages = async (
  providerId: string,
  sourceId: string
): Promise<CurateResourceListItemDto[]> => {
  const pages: CurateResourceListItemDto[] = [];
  let page = 1;
  const pageSize = 200;
  while (true) {
    const data = await getCurateSourcePages({
      providerId,
      sourceId,
      page,
      pageSize,
    });
    pages.push(...(data.pages ?? []));
    if (pages.length >= (data.totalCount ?? pages.length) || (data.pages?.length ?? 0) === 0) {
      break;
    }
    page += 1;
  }
  return pages;
};

const formatActivityTime = (value?: string): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const activityTypeLabel = (eventType?: string): string => {
  switch ((eventType || "").toLowerCase()) {
    case "recommended":
      return "Recommended";
    case "clicked":
      return "Clicked";
    default:
      return eventType || "—";
  }
};

const emptySourceForm = (): SourceForm => ({
  name: "",
  url: "",
  crawlChildren: true,
  maxPages: 500,
});

const CurateResources = () => {
  const [directory, setDirectory] = useState<CurateAdminDirectoryDto | null>(null);
  const [providerId, setProviderId] = useState("mesma-url-bank");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedResource, setSelectedResource] = useState<CurateResourceListItemDto | null>(null);
  const [accessScope, setAccessScope] = useState<AccessScope>("resource");
  const [accessCategories, setAccessCategories] = useState<Category<string>[]>([]);
  const [accessSearch, setAccessSearch] = useState("");
  const [accessPage, setAccessPage] = useState(1);
  const [accessTotal, setAccessTotal] = useState(0);
  const [activeFolderId, setActiveFolderId] = useState<string>("");
  const [accessBusy, setAccessBusy] = useState(false);
  const [pendingSelectAll, setPendingSelectAll] = useState<PendingSelectAll | null>(null);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [renameFolder, setRenameFolder] = useState<{ id: string; name: string } | null>(null);
  const [renameResource, setRenameResource] = useState<CurateResourceListItemDto | null>(null);
  const [sourceForm, setSourceForm] = useState<SourceForm | null>(null);
  const [sourceBusy, setSourceBusy] = useState(false);
  const [expandedSummaries, setExpandedSummaries] = useState<string[]>([]);
  const [expandedPageIds, setExpandedPageIds] = useState<string[]>([]);
  const [childPages, setChildPages] = useState<Record<string, CurateResourceListItemDto[]>>({});
  const [childLoadingIds, setChildLoadingIds] = useState<string[]>([]);
  const [resourceStats, setResourceStats] = useState<CurateResourceStatsDto | null>(null);
  const [statsBusy, setStatsBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [engagementOpen, setEngagementOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autoExpandedPending = useRef<Set<string>>(new Set());
  const crawlingRef = useRef<Set<string>>(new Set());
  const expandedPageIdsRef = useRef<string[]>([]);

  const providers = directory?.providers ?? [];
  const activeProvider = providers.find((p) => p.id === providerId);
  const supportsManage = activeProvider?.supportsManage ?? false;
  const supportsUpload = activeProvider?.supportsUpload ?? false;
  const supportsUrls = activeProvider?.supportsUrls ?? false;
  const activeFolder = directory?.folders.find((f) => f.id === activeFolderId);

  const loadDirectory = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const data = await getCurateAdminDirectory({
        providerId,
        q: search || undefined,
        page,
        pageSize: PAGE_SIZE,
        folderId: activeFolderId || undefined,
      });
      setDirectory(data);
      setExpandedSummaries((current) => {
        const ids = new Set(data.resources.map((resource) => resource.id));
        const kept = current.filter((id) => ids.has(id));
        const toAdd: string[] = [];
        for (const resource of data.resources) {
          if (!isPendingSummary(resource) && !isFailedSummary(resource)) {
            autoExpandedPending.current.delete(resource.id);
            continue;
          }
          if (
            !autoExpandedPending.current.has(resource.id) &&
            !kept.includes(resource.id)
          ) {
            autoExpandedPending.current.add(resource.id);
            toAdd.push(resource.id);
          }
        }
        return toAdd.length ? [...kept, ...toAdd] : kept;
      });
      if (!activeFolderId && data.folders[0]) {
        setActiveFolderId(data.folders[0].id);
      }
      if (data.providers.length && !data.providers.some((p) => p.id === providerId)) {
        setProviderId(data.providers[0].id);
      }
    } catch {
      if (!silent) toast.error("Failed to load Curate directory");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [providerId, search, page, activeFolderId]);

  const selectedResourceId = selectedResource?.id ?? "";
  const selectedResourceProviderId = selectedResource?.providerId ?? "";
  const historyEvents = resourceStats?.activity ?? [];

  useEffect(() => {
    setHistoryOpen(false);
  }, [selectedResourceId]);

  const closeEngagementPane = () => {
    setEngagementOpen(false);
    setHistoryOpen(false);
  };

  const selectResource = (resource: CurateResourceListItemDto, scope: AccessScope = "resource") => {
    setSelectedResource(resource);
    setAccessScope(scope);
    setAccessPage(1);
  };

  useEffect(() => {
    if (!selectedResourceId || !selectedResourceProviderId) {
      setResourceStats(null);
      setStatsBusy(false);
      return;
    }

    let cancelled = false;
    setStatsBusy(true);
    getCurateResourceStats(selectedResourceProviderId, selectedResourceId)
      .then((stats) => {
        if (!cancelled) setResourceStats(stats);
      })
      .catch(() => {
        if (!cancelled) setResourceStats(null);
      })
      .finally(() => {
        if (!cancelled) setStatsBusy(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedResourceId, selectedResourceProviderId]);

  const loadAccess = useCallback(async () => {
    if (accessScope === "resource" || accessScope === "site") {
      if (!selectedResourceId) {
        setAccessCategories([]);
        setAccessTotal(0);
        return;
      }

      try {
        const data = await getCurateAccessClients({
          providerId: selectedResourceProviderId,
          resourceId: selectedResourceId,
          q: accessSearch || undefined,
          page: accessPage,
          pageSize: ACCESS_PAGE_SIZE,
          includeChildren: accessScope === "site",
        });
        setAccessTotal(data.totalCount);
        setAccessCategories([
          {
            id: "clients",
            title: "Clients",
            data: data.clients.map((c) => ({
              id: c.id,
              text: c.name,
              value: c.id,
              selected: c.hasAccess,
              disabled: false,
            })),
          },
        ]);
      } catch {
        toast.error("Failed to load client access");
      }
      return;
    }

    if (!activeFolderId) {
      setAccessCategories([]);
      setAccessTotal(0);
      return;
    }

    try {
      const data = await getCurateFolderAccessClients({
        providerId,
        folderId: activeFolderId,
        q: accessSearch || undefined,
        page: accessPage,
        pageSize: ACCESS_PAGE_SIZE,
      });
      setAccessTotal(data.totalCount);
      setAccessCategories([
        {
          id: "clients",
          title: "Clients",
          data: data.clients.map((c) => ({
            id: c.id,
            text: c.name,
            value: c.id,
            selected: c.hasAccess,
            disabled: false,
          })),
        },
      ]);
    } catch {
      toast.error("Failed to load folder access");
    }
  }, [
    accessScope,
    selectedResourceId,
    selectedResourceProviderId,
    activeFolderId,
    providerId,
    accessSearch,
    accessPage,
  ]);

  useEffect(() => {
    loadDirectory();
  }, [loadDirectory]);

  const pendingSummaryCount = (directory?.resources ?? []).filter(isPendingSummary).length;
  const pendingCrawlCount = (directory?.resources ?? []).filter(isCrawlActive).length;
  const pendingChildSummaryCount = Object.values(childPages)
    .flat()
    .filter(isPendingSummary).length;

  expandedPageIdsRef.current = expandedPageIds;
  const resourcesRef = useRef(directory?.resources);
  resourcesRef.current = directory?.resources;

  const loadChildPages = useCallback(async (resource: CurateResourceListItemDto) => {
    setChildLoadingIds((current) =>
      current.includes(resource.id) ? current : [...current, resource.id]
    );
    try {
      const data = await getCurateSourcePages({
        providerId: resource.providerId,
        sourceId: resource.id,
        pageSize: 100,
      });
      setChildPages((current) => ({ ...current, [resource.id]: data.pages }));
    } catch {
      toast.error("Could not load indexed pages");
    } finally {
      setChildLoadingIds((current) => current.filter((id) => id !== resource.id));
    }
  }, []);

  useEffect(() => {
    if (pendingSummaryCount === 0 && pendingCrawlCount === 0 && pendingChildSummaryCount === 0) {
      return;
    }

    const tick = async () => {
      const active = (resourcesRef.current ?? []).filter(isCrawlActive);
      for (const resource of active) {
        if (crawlingRef.current.has(resource.id)) continue;
        crawlingRef.current.add(resource.id);
        try {
          const result = await crawlCurateSource({
            providerId: resource.providerId,
            sourceId: resource.id,
          });
          setDirectory((prev) =>
            prev
              ? {
                  ...prev,
                  resources: prev.resources.map((row) =>
                    row.id === resource.id
                      ? {
                          ...row,
                          crawlStatus: result.crawlStatus,
                          childCount: result.childCount ?? row.childCount,
                        }
                      : row
                  ),
                }
              : prev
          );
          if (expandedPageIdsRef.current.includes(resource.id)) {
            await loadChildPages(resource);
          }
        } catch {
          setDirectory((prev) =>
            prev
              ? {
                  ...prev,
                  resources: prev.resources.map((row) =>
                    row.id === resource.id ? { ...row, crawlStatus: "failed" } : row
                  ),
                }
              : prev
          );
        } finally {
          crawlingRef.current.delete(resource.id);
        }
      }
      await loadDirectory({ silent: true });
      await Promise.all(
        expandedPageIdsRef.current
          .map((id) => (resourcesRef.current ?? []).find((row) => row.id === id))
          .filter((row): row is CurateResourceListItemDto => !!row)
          .map((row) => loadChildPages(row))
      );
    };

    const timer = window.setInterval(() => {
      void tick();
    }, 5000);
    if (pendingCrawlCount > 0) {
      void tick();
    }
    return () => window.clearInterval(timer);
  }, [pendingSummaryCount, pendingCrawlCount, pendingChildSummaryCount, loadDirectory, loadChildPages]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  useEffect(() => {
    if (accessScope !== "site") {
      return;
    }
    if (selectedResource?.parentId || !(selectedResource?.childCount)) {
      setAccessScope("resource");
    }
  }, [accessScope, selectedResource?.parentId, selectedResource?.childCount]);

  const providerItems: IDropDownListItem[] = useMemo(
    () =>
      providers.map((p: CurateProviderInfoDto) => ({
        text: p.name,
        value: p.id,
        colour: undefined,
        textColour: undefined,
      })),
    [providers]
  );

  const resources = useMemo(() => {
    const all = directory?.resources ?? [];
    if (!activeFolderId) return all;
    return all.filter((r) => r.folderId === activeFolderId);
  }, [directory?.resources, activeFolderId]);

  const pageCount = directory?.totalCount
    ? Math.ceil(directory.totalCount / PAGE_SIZE)
    : 0;
  const accessPageCount = accessTotal ? Math.ceil(accessTotal / ACCESS_PAGE_SIZE) : 0;

  const canShowAccess =
    !!selectedResource &&
    (accessScope === "resource" || accessScope === "site" || !!activeFolderId);

  const handleAccessChange = (updated: Category<string>) => {
    const oldCat = accessCategories.find((c) => c.id === updated.id);
    const changed =
      oldCat == null
        ? []
        : updated.data.filter((item) => {
            const oldItem = oldCat.data.find((i) => i.id === item.id);
            return !!oldItem && oldItem.selected !== item.selected;
          });

    setAccessCategories((current) =>
      current.map((cat) => (cat.id === updated.id ? updated : cat))
    );

    if (changed.length === 0) return;

    if ((accessScope === "resource" || accessScope === "site") && selectedResource) {
      const resource = selectedResource;
      const includeChildren = accessScope === "site";
      const toAdd = changed.filter((c) => c.selected).map((c) => c.value);
      const toRemove = changed.filter((c) => !c.selected).map((c) => c.value);

      const applyGrant = async () => {
        let last: { grantCount: number; resourceId: string } | null = null;
        if (toAdd.length) {
          last = await grantCurateAccess({
            resourceId: resource.id,
            providerId: resource.providerId,
            clientIds: toAdd,
            add: true,
            includeChildren,
          });
        }
        if (toRemove.length) {
          last = await grantCurateAccess({
            resourceId: resource.id,
            providerId: resource.providerId,
            clientIds: toRemove,
            add: false,
            includeChildren,
          });
        }
        return last;
      };

      applyGrant()
        .then((result) => {
          if (!result) return;
          const { grantCount, resourceId } = result;
          setDirectory((prev) =>
            prev
              ? {
                  ...prev,
                  resources: prev.resources.map((r) =>
                    r.id === resourceId ? { ...r, grantCount } : r
                  ),
                }
              : prev
          );
          setSelectedResource((current) =>
            current?.id === resourceId ? { ...current, grantCount } : current
          );
          if (resource.parentId) {
            setChildPages((current) => {
              const siblings = current[resource.parentId!];
              if (!siblings) return current;
              return {
                ...current,
                [resource.parentId!]: siblings.map((row) =>
                  row.id === resourceId ? { ...row, grantCount } : row
                ),
              };
            });
          }
          if (includeChildren) {
            void loadChildPages(resource);
          }
        })
        .catch(() => toast.error("Failed to update access"));
      return;
    }

    if (accessScope === "folder" && activeFolderId) {
      const toAdd = changed.filter((c) => c.selected).map((c) => c.value);
      const toRemove = changed.filter((c) => !c.selected).map((c) => c.value);
      setAccessBusy(true);
      Promise.all([
        toAdd.length
          ? grantCurateFolderAccess({
              providerId,
              folderId: activeFolderId,
              clientIds: toAdd,
              add: true,
            })
          : Promise.resolve(null),
        toRemove.length
          ? grantCurateFolderAccess({
              providerId,
              folderId: activeFolderId,
              clientIds: toRemove,
              add: false,
            })
          : Promise.resolve(null),
      ])
        .then(() => {
          toast.success(
            toAdd.length && !toRemove.length
              ? "Folder access granted"
              : toRemove.length && !toAdd.length
                ? "Folder access revoked"
                : "Folder access updated"
          );
          loadDirectory();
        })
        .catch(() => toast.error("Failed to update folder access"))
        .finally(() => setAccessBusy(false));
    }
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files?.length || !activeFolderId || !supportsManage) return;
    const { allowed, rejected } = splitAllowedFiles(files);
    rejectedFilesToast(rejected);
    const file = allowed[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("folderId", activeFolderId);
    form.append("providerId", providerId);
    form.append("extension", file.name.includes(".") ? `.${file.name.split(".").pop()}` : "");
    try {
      const created = await uploadCurateResource(form);
      if (isPendingSummary(created)) {
        autoExpandedPending.current.add(created.id);
      }
      setDirectory((prev) =>
        prev
          ? {
              ...prev,
              resources: [created, ...prev.resources],
              totalCount: prev.totalCount + 1,
            }
          : prev
      );
      if (isPendingSummary(created)) {
        setExpandedSummaries((current) =>
          current.includes(created.id) ? current : [...current, created.id]
        );
      }
      toast.success("Resource uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  const handleCreateFolder = async (name: string) => {
    if (!name?.trim() || !supportsManage) return;
    try {
      const folder = await createCurateFolder({ providerId, name: name.trim() });
      setDirectory((prev) =>
        prev ? { ...prev, folders: [...prev.folders, folder] } : prev
      );
      setActiveFolderId(folder.id);
      setAccessScope("resource");
      setSelectedResource(null);
      toast.success("Folder created");
    } catch {
      toast.error("Could not create folder");
      throw new Error("Could not create folder");
    }
  };

  const handleDeleteFolder = useCallback(
    async (folderId: string) => {
      if (!folderId || !supportsManage) return;
      try {
        await deleteCurateFolder(providerId, folderId);
        setDirectory((prev) =>
          prev
            ? { ...prev, folders: prev.folders.filter((f) => f.id !== folderId) }
            : prev
        );
        setActiveFolderId((current) => (current === folderId ? "" : current));
        setAccessScope("resource");
        toast.success("Folder deleted");
      } catch {
        toast.error("Could not delete folder (it may not be empty)");
      }
    },
    [providerId, supportsManage]
  );

  const handleClearFolderContents = useCallback(
    async (folderId: string) => {
      if (!folderId || !supportsManage) return;
      try {
        const result = await clearCurateFolderContents(providerId, folderId);
        setChildPages({});
        setExpandedPageIds([]);
        setExpandedSummaries([]);
        if (selectedResource?.folderId === folderId) {
          setSelectedResource(null);
          setResourceStats(null);
        }
        if (activeFolderId === folderId) {
          setPage(1);
          await loadDirectory();
        }
        toast.success(
          result.deletedCount > 0
            ? `Deleted ${result.deletedCount} item${result.deletedCount === 1 ? "" : "s"} from folder`
            : "Folder was already empty"
        );
      } catch {
        toast.error("Could not delete folder contents");
      }
    },
    [
      providerId,
      supportsManage,
      selectedResource?.folderId,
      activeFolderId,
      loadDirectory,
    ]
  );

  const handleRenameFolder = async (name: string) => {
    if (!renameFolder || !supportsManage) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === renameFolder.name) return;
    try {
      const folder = await renameCurateFolder({
        providerId,
        folderId: renameFolder.id,
        name: trimmed,
      });
      setDirectory((prev) =>
        prev
          ? {
              ...prev,
              folders: prev.folders.map((f) => (f.id === folder.id ? folder : f)),
            }
          : prev
      );
      toast.success("Folder renamed");
    } catch {
      toast.error("Could not rename folder");
      throw new Error("Could not rename folder");
    }
  };

  const handleSaveSource = async () => {
    if (!sourceForm || !supportsManage) return;
    const name = sourceForm.name.trim();
    const url = sourceForm.url.trim();
    if (!name || !url) {
      toast.error("Name and URL are required");
      return;
    }
    if (!activeFolderId) {
      toast.error("Choose a folder first");
      return;
    }

    setSourceBusy(true);
    try {
      const created = await createCurateSource({
        providerId,
        folderId: activeFolderId,
        name,
        url,
        isActive: true,
        isApprovedSource: true,
        maxPages: sourceForm.crawlChildren ? sourceForm.maxPages : 1,
        autoDisableChildPages: false,
      });
      setDirectory((prev) =>
        prev
          ? {
              ...prev,
              resources: [created, ...prev.resources],
              totalCount: prev.totalCount + 1,
            }
          : prev
      );
      setSelectedResource(created);
      toast.success("URL source created");
      setSourceForm(null);
    } catch {
      toast.error("Could not create source");
    } finally {
      setSourceBusy(false);
    }
  };

  const patchResource = (updated: CurateResourceListItemDto) => {
    setDirectory((prev) =>
      prev
        ? {
            ...prev,
            resources: prev.resources.map((r) => (r.id === updated.id ? updated : r)),
          }
        : prev
    );
    if (updated.parentId) {
      setChildPages((current) => {
        const siblings = current[updated.parentId!];
        if (!siblings) return current;
        return {
          ...current,
          [updated.parentId!]: siblings.map((row) => (row.id === updated.id ? updated : row)),
        };
      });
    }
    if (selectedResource?.id === updated.id) {
      setSelectedResource(updated);
    }
  };

  const toggleSummary = (resourceId: string) => {
    setExpandedSummaries((current) =>
      current.includes(resourceId)
        ? current.filter((id) => id !== resourceId)
        : [...current, resourceId]
    );
  };

  const allSummariesExpanded =
    resources.length > 0 && resources.every((resource) => expandedSummaries.includes(resource.id));

  const toggleAllSummaries = () => {
    setExpandedSummaries(allSummariesExpanded ? [] : resources.map((resource) => resource.id));
  };

  const togglePages = (resource: CurateResourceListItemDto) => {
    const isOpen = expandedPageIds.includes(resource.id);
    if (isOpen) {
      setExpandedPageIds((current) => current.filter((id) => id !== resource.id));
      return;
    }
    setExpandedPageIds((current) => [...current, resource.id]);
    if (!childPages[resource.id]) {
      void loadChildPages(resource);
    }
  };

  const handleRetrySourceSummaries = async (resource: CurateResourceListItemDto) => {
    if (!supportsManage || sourceBusy) return;
    setSourceBusy(true);
    try {
      const result = await backfillCurateSourceSummaries({
        providerId: resource.providerId,
        sourceId: resource.id,
      });
      if (!expandedPageIds.includes(resource.id)) {
        setExpandedPageIds((current) => [...current, resource.id]);
      }
      await loadChildPages(resource);
      await loadDirectory({ silent: true });
      if (result.queued > 0) {
        toast.success(
          `Queued ${result.queued} summar${result.queued === 1 ? "y" : "ies"} for sub-pages. They will appear when ready.`
        );
      } else {
        toast.info("This URL has no indexed sub-pages to summarise");
      }
    } catch {
      toast.error("Could not retry summaries for this URL");
    } finally {
      setSourceBusy(false);
    }
  };

  const handleRefreshSummary = async (resource: CurateResourceListItemDto) => {
    if (!supportsManage || sourceBusy) return;
    setSourceBusy(true);
    try {
      const updated = await refreshCurateResourceSummary({
        providerId: resource.providerId,
        resourceId: resource.id,
      });
      patchResource(updated);
      toast.success("Summary refresh queued");
    } catch {
      toast.error("Could not refresh summary");
    } finally {
      setSourceBusy(false);
    }
  };

  const handleToggleChildActive = async (child: CurateResourceListItemDto) => {
    if (!supportsManage || sourceBusy) return;
    const next = child.isActive === false;
    setSourceBusy(true);
    try {
      const updated = await updateCurateResource({
        providerId: child.providerId,
        resourceId: child.id,
        isActive: next,
      });
      patchResource(updated);
      await loadDirectory({ silent: true });
      if (child.parentId && expandedPageIds.includes(child.parentId)) {
        const parent = (resourcesRef.current ?? []).find((row) => row.id === child.parentId);
        if (parent) {
          await loadChildPages(parent);
        }
      }
      toast.success(next ? "Page enabled" : "Page disabled");
    } catch {
      toast.error(next ? "Could not enable this page" : "Could not disable this page");
    } finally {
      setSourceBusy(false);
    }
  };

  const handleExportSelected = async () => {
    if (!selectedResource || exportBusy) return;
    setExportBusy(true);
    try {
      const rows = [];
      if (selectedResource.parentId) {
        const parent = (resourcesRef.current ?? []).find(
          (row) => row.id === selectedResource.parentId
        );
        rows.push(toCurateExportRow(selectedResource, parent?.title));
      } else {
        rows.push(toCurateExportRow(selectedResource));
        if ((selectedResource.childCount ?? 0) > 0) {
          const fromStats = resourceStats?.pages ?? [];
          if (fromStats.length > 0) {
            for (const pageStat of fromStats) {
              rows.push({
                title: pageStat.title || "Untitled",
                type: "Page",
                url: pageStat.url,
                parent: selectedResource.title,
                grants: 0,
                recommendations: pageStat.recommendations,
                clicks: pageStat.clicks,
                active: true,
              });
            }
          } else {
            const cached = childPages[selectedResource.id];
            const children =
              cached && cached.length >= (selectedResource.childCount ?? 0)
                ? cached
                : await fetchAllSourcePages(
                    selectedResource.providerId,
                    selectedResource.id
                  );
            for (const child of children) {
              rows.push(toCurateExportRow(child, selectedResource.title));
            }
          }
        }
      }
      downloadCurateEngagementWorkbook({
        filename: curateExportFilename(selectedResource.title),
        resources: rows,
        activity: resourceStats?.activity,
      });
    } catch {
      toast.error("Could not export engagement data");
    } finally {
      setExportBusy(false);
    }
  };

  const folderContextMenuItems: ContextMenuItem[] = useMemo(
    () => [
      {
        title: "Rename",
        onClickHandler: (folderId) => {
          const folder = directory?.folders.find((f) => f.id === folderId);
          if (!folder) return;
          setRenameFolder({ id: folder.id, name: folder.name });
        },
        icon: "/img/edit.svg",
      },
      {
        title: "Delete all items",
        onClickHandler: (folderId) => handleClearFolderContents(folderId),
        icon: "/img/trash-red.svg",
        style: {
          backgroundColor: "#F0DFDE",
          color: "red",
        },
        requiresConfirmation: true,
        confirmationConfig: {
          title: "Delete all items in folder?",
          message:
            "This permanently deletes every URL and page in this folder, including crawled sub-pages. The folder itself will remain. This cannot be undone.",
          severity: "danger",
          icon: "/img/trash-red.svg",
        },
      },
      {
        title: "Delete folder",
        onClickHandler: (folderId) => handleDeleteFolder(folderId),
        icon: "/img/trash-red.svg",
        style: {
          backgroundColor: "#F0DFDE",
          color: "red",
        },
        requiresConfirmation: true,
        confirmationConfig: {
          title: "Confirm Delete",
          message: "Delete this empty folder? This action cannot be undone.",
          severity: "danger",
          icon: "/img/trash-red.svg",
        },
      },
    ],
    [directory?.folders, handleDeleteFolder, handleClearFolderContents]
  );

  const handleDeleteResource = async (resource: CurateResourceListItemDto) => {
    if (!supportsManage) return;
    if (!window.confirm(`Delete "${resource.title}"?`)) return;
    try {
      await deleteCurateResource(resource.providerId, resource.id);
      if (resource.parentId) {
        setChildPages((current) => ({
          ...current,
          [resource.parentId!]: (current[resource.parentId!] ?? []).filter((row) => row.id !== resource.id),
        }));
        setDirectory((prev) =>
          prev
            ? {
                ...prev,
                resources: prev.resources.map((row) =>
                  row.id === resource.parentId
                    ? { ...row, childCount: Math.max(0, (row.childCount ?? 1) - 1) }
                    : row
                ),
              }
            : prev
        );
      } else {
        setDirectory((prev) =>
          prev
            ? {
                ...prev,
                resources: prev.resources.filter((r) => r.id !== resource.id),
                totalCount: Math.max(0, prev.totalCount - 1),
              }
            : prev
        );
        setChildPages((current) => {
          const next = { ...current };
          delete next[resource.id];
          return next;
        });
        setExpandedPageIds((current) => current.filter((id) => id !== resource.id));
      }
      if (selectedResource?.id === resource.id) {
        setSelectedResource(null);
      }
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleRenameResource = async (title: string) => {
    if (!renameResource || !supportsManage) return;
    const trimmed = title.trim();
    if (!trimmed || trimmed === renameResource.title) return;
    try {
      const updated = await updateCurateResource({
        providerId: renameResource.providerId,
        resourceId: renameResource.id,
        title: trimmed,
      });
      setDirectory((prev) =>
        prev
          ? {
              ...prev,
              resources: prev.resources.map((r) => (r.id === updated.id ? updated : r)),
            }
          : prev
      );
      if (selectedResource?.id === updated.id) {
        setSelectedResource(updated);
      }
      toast.success("Resource renamed");
    } catch {
      toast.error("Update failed");
      throw new Error("Update failed");
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <p className={styles.eyebrow}>Mesma Portal</p>
          <h1>Curate resources</h1>
        </div>
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <SearchBar
              value={search}
              onChange={(v) => {
                setSearch(v);
                setPage(1);
              }}
              type=""
            />
          </div>
          {providerItems.length > 1 && (
            <div className={styles.sourceWrap}>
              <DropDownList
                placeholder="Source"
                value={providerId}
                data={providerItems}
                onChange={(item) => {
                  setProviderId(String(item.value));
                  setPage(1);
                  setActiveFolderId("");
                  setSelectedResource(null);
                  setAccessScope("resource");
                }}
              />
            </div>
          )}
        </div>
      </header>

      {supportsManage && supportsUpload && (
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={ALLOWED_UPLOAD_ACCEPT}
          onChange={(e) => {
            handleUpload(e.target.files);
            e.target.value = "";
          }}
        />
      )}

      <div
        className={[
          styles.workspace,
          engagementOpen ? styles.workspaceEngagement : "",
          accessOpen ? styles.workspaceAccess : "",
          historyOpen ? styles.workspaceWithHistory : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <aside className={styles.folders}>
          <div className={styles.panelHeader}>
            <h3>Folders</h3>
            <span className={styles.countBadge}>{directory?.folders.length ?? 0}</span>
          </div>
          <ul className={styles.folderList}>
            {(directory?.folders ?? []).map((folder) => {
              const isActive = folder.id === activeFolderId;
              return (
                <li
                  key={folder.id}
                  className={`${styles.folderRow} ${isActive ? styles.folderRowActive : ""}`}
                >
                  <button
                    type="button"
                    className={isActive ? styles.folderActive : styles.folderBtn}
                    onClick={() => {
                      setActiveFolderId(folder.id);
                      setSelectedResource(null);
                      setAccessScope("resource");
                      setAccessPage(1);
                      setPage(1);
                    }}
                  >
                    <span className={styles.folderName}>{folder.name}</span>
                  </button>
                  {supportsManage && (
                    <div className={styles.folderMenuWrap}>
                      <ContextMenu
                        items={folderContextMenuItems}
                        contextId={folder.id}
                        position="left"
                      >
                        <div className={styles.folderMenu} aria-label={`Folder actions for ${folder.name}`}>
                          <img src="/img/menu-meatballs.svg" alt="" />
                        </div>
                      </ContextMenu>
                    </div>
                  )}
                </li>
              );
            })}
            {supportsManage && (
              <li className={styles.newFolderRow}>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={() => setShowCreateFolder(true)}
                >
                  New folder
                </button>
              </li>
            )}
          </ul>
        </aside>

        <section className={styles.files}>
          <div className={styles.panelHeader}>
            <div>
              <h3>Resources</h3>
              <p className={styles.panelSub}>
                {activeFolder?.name ?? "All folders"}
                {loading ? " · Loading…" : ` · ${resources.length}`}
              </p>
              {supportsManage && supportsUpload && (
                <p className={styles.allowedTypes}>Allowed: {ALLOWED_UPLOAD_LABEL}</p>
              )}
            </div>
            <div className={styles.panelActions}>
              {resources.length > 0 && (
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={toggleAllSummaries}
                >
                  {allSummariesExpanded ? "Collapse all" : "Expand all"}
                </button>
              )}
              <button
                type="button"
                className={engagementOpen ? styles.btnPrimary : styles.btnSecondary}
                onClick={() =>
                  engagementOpen ? closeEngagementPane() : setEngagementOpen(true)
                }
                aria-expanded={engagementOpen}
                disabled={!selectedResource}
                title={
                  selectedResource
                    ? undefined
                    : "Select a resource first"
                }
              >
                Engagement
              </button>
              <button
                type="button"
                className={accessOpen ? styles.btnPrimary : styles.btnSecondary}
                onClick={() => setAccessOpen((open) => !open)}
                aria-expanded={accessOpen}
                disabled={!selectedResource}
                title={
                  selectedResource
                    ? undefined
                    : "Select a resource first"
                }
              >
                Client access
              </button>
              {supportsManage && supportsUrls && (
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => {
                    setSourceForm(emptySourceForm());
                  }}
                  disabled={!activeFolderId}
                >
                  Add URL
                </button>
              )}
              {supportsManage && supportsUpload && (
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!activeFolderId}
                  title={`Allowed: ${ALLOWED_UPLOAD_LABEL}`}
                >
                  Upload
                </button>
              )}
            </div>
          </div>

          {resources.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No resources in this folder yet.</p>
              {supportsUpload && (
                <p className={styles.allowedTypes}>Allowed: {ALLOWED_UPLOAD_LABEL}</p>
              )}
              {supportsManage && activeFolderId && (
                <>
                  {supportsUrls && (
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={() => {
                        setSourceForm(emptySourceForm());
                      }}
                    >
                      Add a URL
                    </button>
                  )}
                  {supportsUpload && (
                    <button
                      type="button"
                      className={styles.btnPrimary}
                      onClick={() => fileInputRef.current?.click()}
                      title={`Allowed: ${ALLOWED_UPLOAD_LABEL}`}
                    >
                      Upload a file
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <ul className={styles.fileList}>
              {resources.map((resource) => {
                const isSelected =
                  (accessScope === "resource" || accessScope === "site") &&
                  selectedResource?.id === resource.id;
                const ext =
                  (resource.extension || "").replace(/^\./, "").toUpperCase() ||
                  (resource.kind === "source" || resource.kind === "link"
                    ? "WEB"
                    : (resource.kind || "").replace(/^\./, "").toUpperCase() || "FILE");
                const isExpanded = expandedSummaries.includes(resource.id);
                const abstract = resourceAbstract(resource);
                const pending = isPendingSummary(resource);
                const failed = isFailedSummary(resource);
                const pagesOpen = expandedPageIds.includes(resource.id);
                const childCount = resource.childCount ?? 0;
                const crawlStatus = (resource.crawlStatus || "").toLowerCase();
                const canExpandPages =
                  supportsUrls &&
                  (childCount > 0 ||
                    isCrawlActive(resource) ||
                    crawlStatus === "failed" ||
                    crawlStatus === "complete");
                const children = childPages[resource.id] ?? [];
                const childrenLoading = childLoadingIds.includes(resource.id);
                const hasFailedSummaries =
                  failed || children.some((child) => isFailedSummary(child));
                return (
                  <React.Fragment key={resource.id}>
                  <li
                    className={isSelected ? styles.fileActive : styles.fileRow}
                  >
                    <div className={styles.fileMain}>
                      {canExpandPages && (
                        <button
                          type="button"
                          className={styles.expandToggle}
                          onClick={() => togglePages(resource)}
                          aria-expanded={pagesOpen}
                          aria-label={pagesOpen ? "Hide indexed pages" : "Show indexed pages"}
                        >
                          {pagesOpen ? "▾" : "▸"}
                        </button>
                      )}
                      <button
                        type="button"
                        className={styles.fileSelect}
                        onClick={() => {
                          selectResource(resource);
                        }}
                      >
                        <span className={styles.extBadge}>{ext}</span>
                        <span className={styles.fileBody}>
                          <span className={styles.fileTitle}>{resource.title}</span>
                          {compactResourceMeta(resource) ? (
                            <span className={styles.fileMeta}>
                              {compactResourceMeta(resource)}
                            </span>
                          ) : null}
                        </span>
                      </button>
                      <div className={styles.fileActions}>
                        {resource.url ? (
                          <button
                            type="button"
                            className={styles.rowAction}
                            onClick={(event) => openExternalUrl(resource.url!, event)}
                          >
                            Open
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className={styles.rowAction}
                          onClick={() => toggleSummary(resource.id)}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? "Hide" : "Summary"}
                        </button>
                        {supportsManage &&
                          supportsUrls &&
                          !resource.parentId &&
                          hasFailedSummaries && (
                          <button
                            type="button"
                            className={styles.rowAction}
                            onClick={() => handleRetrySourceSummaries(resource)}
                            disabled={sourceBusy || isCrawlActive(resource)}
                          >
                            Retry summaries
                          </button>
                        )}
                        {supportsManage && (
                          <button
                            type="button"
                            className={styles.rowActionDanger}
                            onClick={() => handleDeleteResource(resource)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                    {isExpanded && (
                      pending && !abstract ? (
                        <div className={styles.fileSummaryProgress}>
                          {resource.url ? (
                            <ResourceUrlLink url={resource.url} className={styles.summaryUrl} />
                          ) : null}
                          <PendingSummaryProgress resource={resource} />
                        </div>
                      ) : failed && !abstract ? (
                        <div className={styles.fileSummary}>
                          <p className={styles.summaryFullText}>
                            A summary could not be generated for this resource.
                          </p>
                          {supportsManage && (
                            <button
                              type="button"
                              className={styles.btnSecondary}
                              onClick={() => handleRefreshSummary(resource)}
                              disabled={sourceBusy}
                            >
                              Try again
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className={styles.fileSummary}>
                          {resource.url ? (
                            <ResourceUrlLink url={resource.url} className={styles.summaryUrl} />
                          ) : null}
                          <p className={styles.summaryFullText}>
                            {abstract || summaryEmptyMessage(resource)}
                          </p>
                          <ResourceOutline outline={resource.outline} />
                        </div>
                      )
                    )}
                  </li>
                  {pagesOpen && (
                    <ul className={styles.childList}>
                      {childrenLoading && children.length === 0 ? (
                        <li className={styles.childEmpty}>Indexing pages…</li>
                      ) : children.length === 0 ? (
                        <li className={styles.childEmpty}>
                          {isCrawlActive(resource)
                            ? "Indexing pages…"
                            : "No sub-pages indexed yet."}
                        </li>
                      ) : (
                        children.map((child) => {
                          const childSelected =
                            accessScope === "resource" && selectedResource?.id === child.id;
                          const childExpanded = expandedSummaries.includes(child.id);
                          const childAbstract = resourceAbstract(child);
                          const childPending = isPendingSummary(child);
                          const childFailed = isFailedSummary(child);
                          const childDisabled = child.isActive === false;
                          return (
                            <li
                              key={child.id}
                              className={`${childSelected ? styles.fileActive : styles.fileRow} ${styles.fileNested} ${childDisabled ? styles.fileDisabled : ""}`}
                            >
                              <div className={styles.fileMain}>
                                <button
                                  type="button"
                                  className={styles.fileSelect}
                                  onClick={() => {
                                    selectResource(child);
                                  }}
                                >
                                  <span className={styles.extBadge}>
                                    {(child.extension || "").replace(/^\./, "").toUpperCase() === "PDF" ||
                                    (child.url || "").toLowerCase().includes(".pdf")
                                      ? "PDF"
                                      : "PAGE"}
                                  </span>
                                  <span className={styles.fileBody}>
                                    <span className={styles.fileTitle}>{child.title}</span>
                                    {compactResourceMeta(child, true) ? (
                                      <span className={styles.fileMeta}>
                                        {compactResourceMeta(child, true)}
                                      </span>
                                    ) : null}
                                  </span>
                                </button>
                                <div className={styles.fileActions}>
                                  {child.url ? (
                                    <button
                                      type="button"
                                      className={styles.rowAction}
                                      onClick={(event) => openExternalUrl(child.url!, event)}
                                    >
                                      Open
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    className={styles.rowAction}
                                    onClick={() => toggleSummary(child.id)}
                                    aria-expanded={childExpanded}
                                  >
                                    {childExpanded ? "Hide" : "Summary"}
                                  </button>
                                  {supportsManage && (
                                    <button
                                      type="button"
                                      className={styles.rowAction}
                                      onClick={() => handleToggleChildActive(child)}
                                      disabled={sourceBusy}
                                    >
                                      {childDisabled ? "Enable" : "Disable"}
                                    </button>
                                  )}
                                  {supportsManage && (
                                    <button
                                      type="button"
                                      className={styles.rowActionDanger}
                                      onClick={() => handleDeleteResource(child)}
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </div>
                              {childExpanded && (
                                childPending && !childAbstract ? (
                                  <div className={styles.fileSummaryProgress}>
                                    {child.url ? (
                                      <ResourceUrlLink url={child.url} className={styles.summaryUrl} />
                                    ) : null}
                                    <PendingSummaryProgress resource={child} />
                                  </div>
                                ) : childFailed && !childAbstract ? (
                                  <div className={styles.fileSummary}>
                                    <p className={styles.summaryFullText}>
                                      A summary could not be generated for this page.
                                    </p>
                                    {supportsManage && (
                                      <button
                                        type="button"
                                        className={styles.btnSecondary}
                                        onClick={() => handleRefreshSummary(child)}
                                        disabled={sourceBusy}
                                      >
                                        Try again
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <div className={styles.fileSummary}>
                                    {child.url ? (
                                      <ResourceUrlLink url={child.url} className={styles.summaryUrl} />
                                    ) : null}
                                    <p className={styles.summaryFullText}>
                                      {childAbstract || summaryEmptyMessage(child)}
                                    </p>
                                    <ResourceOutline outline={child.outline} />
                                  </div>
                                )
                              )}
                            </li>
                          );
                        })
                      )}
                    </ul>
                  )}
                  </React.Fragment>
                );
              })}
            </ul>
          )}

          {pageCount > 1 && (
            <div className={styles.pagingWrap}>
              <Paging activePage={page} pageCount={pageCount} pageChanged={setPage} />
            </div>
          )}
        </section>

        {engagementOpen && (
        <section className={styles.engagement} aria-label="Engagement">
          <div className={styles.panelHeader}>
            <div>
              <h3>Engagement</h3>
              <p className={styles.panelSub}>
                {selectedResource ? selectedResource.title : "Select a resource"}
              </p>
            </div>
            <div className={styles.panelActions}>
              {selectedResource && (
                <>
                  <button
                    type="button"
                    className={historyOpen ? styles.btnPrimary : styles.btnSecondary}
                    onClick={() => setHistoryOpen((open) => !open)}
                    aria-expanded={historyOpen}
                    disabled={statsBusy && !resourceStats}
                  >
                    History{historyEvents.length ? ` (${historyEvents.length})` : ""}
                  </button>
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={handleExportSelected}
                    disabled={exportBusy}
                  >
                    {exportBusy ? "Exporting…" : "Export Excel"}
                  </button>
                </>
              )}
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={closeEngagementPane}
              >
                Close
              </button>
            </div>
          </div>
          {!selectedResource ? (
            <div className={styles.emptyState}>
              <p>Select a resource to view recommendations, clicks, and CTR.</p>
            </div>
          ) : (
            <div className={styles.engagementBody}>
              {statsBusy && !resourceStats ? (
                <p className={styles.accessHint}>Loading engagement…</p>
              ) : (
                <div className={styles.statRow}>
                  <div className={styles.statCard}>
                    <span className={styles.statValue}>
                      {resourceStats?.recommendations ?? selectedResource.recommendationCount ?? 0}
                    </span>
                    <span className={styles.statLabel}>Recommendations</span>
                  </div>
                  <div className={styles.statCard}>
                    <span className={styles.statValue}>
                      {resourceStats?.clicks ?? selectedResource.clickCount ?? 0}
                    </span>
                    <span className={styles.statLabel}>Click-throughs</span>
                  </div>
                  <div className={styles.statCard}>
                    <span className={styles.statValue}>
                      {formatCtr(
                        resourceStats?.recommendations ?? selectedResource.recommendationCount ?? 0,
                        resourceStats?.clicks ?? selectedResource.clickCount ?? 0
                      )}
                    </span>
                    <span className={styles.statLabel}>CTR</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
        )}

        {accessOpen && (
        <section className={styles.access} aria-label="Client access">
          <div className={styles.panelHeader}>
            <div>
              <h3>Client access</h3>
              <p className={styles.panelSub}>
                {selectedResource
                  ? accessScope === "folder" && activeFolder
                    ? `All resources in “${activeFolder.name}”`
                    : accessScope === "site"
                      ? `“${selectedResource.title}” and all indexed pages`
                      : selectedResource.title
                  : "Choose a resource to manage grants"}
              </p>
            </div>
            <div className={styles.panelActions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setAccessOpen(false)}
              >
                Close
              </button>
            </div>
          </div>

          {selectedResource && (
            <div className={styles.scopeToggle}>
              <button
                type="button"
                className={
                  accessScope === "resource" ? styles.scopeActive : styles.scopeBtn
                }
                onClick={() => {
                  setAccessScope("resource");
                  setAccessPage(1);
                }}
              >
                {selectedResource.parentId ? "This page" : "This URL"}
              </button>
              {!selectedResource.parentId && (selectedResource.childCount ?? 0) > 0 && (
                <button
                  type="button"
                  className={accessScope === "site" ? styles.scopeActive : styles.scopeBtn}
                  onClick={() => {
                    setAccessScope("site");
                    setAccessPage(1);
                  }}
                >
                  This site and all pages
                </button>
              )}
              <button
                type="button"
                className={accessScope === "folder" ? styles.scopeActive : styles.scopeBtn}
                onClick={() => {
                  setAccessScope("folder");
                  setAccessPage(1);
                }}
                disabled={!activeFolderId}
              >
                Entire folder
              </button>
            </div>
          )}

          {!canShowAccess ? (
            <div className={styles.emptyState}>
              <p>Select a resource from the list to grant or revoke client access.</p>
            </div>
          ) : (
            <div className={styles.accessBody}>
              {accessScope === "folder" && (
                <p className={styles.accessHint}>
                  Ticking a client grants them access to every resource in this folder,
                  including indexed pages. Unticking removes them from every resource.
                  Checked means they already have access to all files here.
                </p>
              )}
              {accessScope === "site" && (
                <p className={styles.accessHint}>
                  Ticking a client grants the root URL and every indexed page. Unticking
                  removes them from all of those pages. Select a single page in the list
                  to mandate or revoke that page only.
                </p>
              )}
              {accessScope === "resource" && selectedResource.parentId && (
                <p className={styles.accessHint}>
                  This grants access to this page only. Use the root URL to mandate or
                  remove every indexed page at once.
                </p>
              )}
              {accessScope === "resource" && !selectedResource.parentId && (selectedResource.childCount ?? 0) > 0 && (
                <p className={styles.accessHint}>
                  This grants the root URL only, not its indexed pages. Use “This site
                  and all pages” to mandate or remove every page at once.
                </p>
              )}
              <div className={styles.accessSearch}>
                <SearchBar
                  value={accessSearch}
                  onChange={(v) => {
                    setAccessSearch(v);
                    setAccessPage(1);
                  }}
                  type=""
                />
              </div>
              <div className={styles.accessList}>
                <SelectionListPane
                  categories={accessCategories}
                  onChange={handleAccessChange}
                  selectAllDisabled={accessBusy}
                  requestSelectAll={(selecting, apply) => {
                    if ((accessCategories[0]?.data.length ?? 0) === 0) return;
                    setPendingSelectAll({ selecting, apply });
                  }}
                  maxHeight="100%"
                />
              </div>
              {accessPageCount > 1 && (
                <div className={styles.pagingWrap}>
                  <Paging
                    activePage={accessPage}
                    pageCount={accessPageCount}
                    pageChanged={setAccessPage}
                  />
                </div>
              )}
            </div>
          )}
        </section>
        )}
        {historyOpen && selectedResource && (
          <aside className={styles.historyPane} aria-label="Engagement history">
            <div className={styles.panelHeader}>
              <div>
                <h3>History</h3>
                <p className={styles.panelSub}>{selectedResource.title}</p>
              </div>
              <div className={styles.panelActions}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => setHistoryOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
            {statsBusy && historyEvents.length === 0 ? (
              <p className={styles.historyEmpty}>Loading engagement…</p>
            ) : historyEvents.length === 0 ? (
              <p className={styles.historyEmpty}>
                No recommendation or click events yet.
              </p>
            ) : (
              <ul className={styles.historyList}>
                {historyEvents.map((event) => {
                  const kind = (event.eventType || "").toLowerCase();
                  return (
                    <li key={event.id} className={styles.historyTile}>
                      <div className={styles.historyTileHead}>
                        <span
                          className={`${styles.historyType} ${
                            kind === "clicked"
                              ? styles.historyTypeClicked
                              : styles.historyTypeRecommended
                          }`}
                        >
                          {activityTypeLabel(event.eventType)}
                        </span>
                        <span className={styles.historyWhen}>
                          {formatActivityTime(event.timestamp)}
                        </span>
                      </div>
                      <div className={styles.historyUser}>
                        {event.userName || event.userSub || "Unknown user"}
                      </div>
                      <div className={styles.historyOrg}>
                        {event.organisationName || event.clientId || "Unknown organisation"}
                      </div>
                      {event.url ? (
                        <a
                          href={event.url}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.historyUrl}
                        >
                          {event.url}
                        </a>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>
        )}
      </div>

      <PromptAction
        isVisible={showCreateFolder}
        onClose={() => setShowCreateFolder(false)}
        clickHandler={handleCreateFolder}
        title="Create new folder"
        message="Enter a name for the folder in this resource bank."
        placeholder="Folder name"
        confirmText="Create folder"
        cancelText="Cancel"
        requiredMessage="Folder name is required"
        severity="warning"
      />

      <PromptAction
        isVisible={!!renameFolder}
        onClose={() => setRenameFolder(null)}
        clickHandler={handleRenameFolder}
        title="Rename folder"
        message="Enter a new name for this folder."
        placeholder="Folder name"
        defaultValue={renameFolder?.name ?? ""}
        confirmText="Rename"
        cancelText="Cancel"
        requiredMessage="Folder name is required"
        severity="warning"
      />

      <PromptAction
        isVisible={!!renameResource}
        onClose={() => setRenameResource(null)}
        clickHandler={handleRenameResource}
        title="Rename resource"
        message="Enter a new title for this resource."
        placeholder="Title"
        defaultValue={renameResource?.title ?? ""}
        confirmText="Rename"
        cancelText="Cancel"
        requiredMessage="Title is required"
        severity="warning"
      />

      <ConfirmAction
        isVisible={!!pendingSelectAll}
        onClose={() => setPendingSelectAll(null)}
        clickHandler={() => {
          pendingSelectAll?.apply();
        }}
        title={
          pendingSelectAll?.selecting
            ? "Grant access to all listed clients?"
            : "Revoke access for all listed clients?"
        }
        message={
          pendingSelectAll?.selecting
            ? accessScope === "folder"
              ? `This will grant every listed client access to all resources in “${activeFolder?.name ?? "this folder"}”.`
              : accessScope === "site"
                ? `This will grant every listed client the root URL and every indexed page under “${selectedResource?.title ?? "this site"}”.`
                : `This will grant every listed client access to “${selectedResource?.title ?? "this resource"}”.`
            : accessScope === "folder"
              ? `This will revoke every listed client’s access from all resources in “${activeFolder?.name ?? "this folder"}”.`
              : accessScope === "site"
                ? `This will revoke every listed client from the root URL and every indexed page under “${selectedResource?.title ?? "this site"}”.`
                : `This will revoke every listed client’s access from “${selectedResource?.title ?? "this resource"}”.`
        }
        confirmText={pendingSelectAll?.selecting ? "Grant access" : "Revoke access"}
        cancelText="Cancel"
        severity="warning"
      />

      {sourceForm && (
        <div className={styles.modalOverlay} onClick={() => !sourceBusy && setSourceForm(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Add URL source</h3>
            <label>
              Resource name
              <input
                value={sourceForm.name}
                onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })}
              />
            </label>
            <label>
              Website URL
              <input
                list="tlipss-urls"
                value={sourceForm.url}
                onChange={(e) => setSourceForm({ ...sourceForm, url: e.target.value })}
                placeholder="https://"
              />
              <datalist id="tlipss-urls">
                {TLIPSS_URLS.map((url) => (
                  <option key={url} value={url} />
                ))}
              </datalist>
            </label>
            <label className={styles.checkRow}>
              <input
                type="checkbox"
                checked={sourceForm.crawlChildren}
                onChange={(e) =>
                  setSourceForm({ ...sourceForm, crawlChildren: e.target.checked })
                }
              />
              Index sub-pages under this URL
            </label>
            {sourceForm.crawlChildren && (
              <label>
                Maximum pages
                <input
                  type="number"
                  min={2}
                  max={500}
                  value={sourceForm.maxPages}
                  onChange={(e) =>
                    setSourceForm({
                      ...sourceForm,
                      maxPages: Math.min(500, Math.max(2, Number(e.target.value) || 500)),
                    })
                  }
                />
              </label>
            )}
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setSourceForm(null)}
                disabled={sourceBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSaveSource}
                disabled={sourceBusy}
              >
                {sourceBusy ? "Saving…" : "Create URL"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurateResources;
